/* eslint-disable @typescript-eslint/naming-convention, jsdoc/require-param, jsdoc/require-returns */
import { findChildByName, getPropertyValue, cleanFBXName } from "../types/fbxTypes";
import { getChildren } from "./connections";
import { extractUserProperties, getPropertyEntries, getPropertyTemplate, resolvePropertyValue, resolvePropertyValues, } from "./propertyTemplates";
import { resolveMaterialModel } from "./materialModel";
/**
 * Extract material data from an FBX Material node.
 */
export function extractMaterial(materialNode, materialId, objectMap, templates) {
    const name = cleanFBXName(getPropertyValue(materialNode, 1) ?? "Material");
    const template = getMaterialTemplate(materialNode, templates);
    // Determine Lambert vs Phong from ShadingModel property
    const shadingModel = findChildByName(materialNode, "ShadingModel");
    const shadingType = shadingModel
        ? (getPropertyValue(shadingModel, 0) ?? "Lambert")
        : (resolvePropertyValue(materialNode, template, "ShadingModel") ?? "Lambert");
    const type = shadingType.toLowerCase() === "phong" ? "Phong" : "Lambert";
    // Extract properties from Properties70
    const properties = extractMaterialProperties(materialNode, template);
    // Find connected textures
    const textureTemplate = templates ? (getPropertyTemplate(templates, "Texture", "FbxFileTexture") ?? getPropertyTemplate(templates, "Texture")) : undefined;
    const textures = extractTextures(materialId, objectMap, textureTemplate);
    // Resolve the unified material model (classic + PBR parameters) from all properties, textures and shader bindings.
    const props = new Map();
    for (const tp of template?.properties.values() ?? []) {
        props.set(tp.name, { type: tp.propertyType, values: tp.values });
    }
    for (const entry of getPropertyEntries(materialNode)) {
        props.set(entry.name, { type: entry.type, values: entry.values });
    }
    const texturesByProp = new Map();
    for (const tex of textures) {
        if (!texturesByProp.has(tex.propertyName)) {
            texturesByProp.set(tex.propertyName, tex);
        }
    }
    const source = { shadingModelName: shadingType, props, texturesByProp, shader: extractShaderInfo(materialId, objectMap) };
    const model = resolveMaterialModel(source);
    return { id: materialId, name, type, properties, textures, model, userProperties: extractUserProperties(materialNode) };
}
/**
 * Shader implementation connected to a material (Arnold, OSL / Standard Surface, Stingray PBS): the RenderAPI names
 * the flavour and the binding table maps shader semantics to material property names.
 */
function extractShaderInfo(materialId, objectMap) {
    // The material is connected as a child of its Implementation (Material -> Implementation), so look at parents.
    const implementations = objectMap.connections
        .filter((conn) => conn.type === "OO" && conn.childId === materialId && objectMap.objects.get(conn.parentId)?.name === "Implementation")
        .map((conn) => ({ id: conn.parentId, node: objectMap.objects.get(conn.parentId) }));
    if (implementations.length === 0) {
        return undefined;
    }
    const impl = implementations[0];
    const renderApi = getPropertyEntries(impl.node).find((e) => e.name === "RenderAPI")?.values[0];
    const bindings = new Map();
    const add = (shaderProp, materialProp) => {
        const list = bindings.get(shaderProp);
        if (list) {
            list.push(materialProp);
        }
        else {
            bindings.set(shaderProp, [materialProp]);
        }
    };
    for (const table of getChildren(objectMap, impl.id, "BindingTable")) {
        for (const entry of table.node.children) {
            if (entry.name !== "Entry") {
                continue;
            }
            const src = getPropertyValue(entry, 0);
            const srcType = getPropertyValue(entry, 1);
            const dst = getPropertyValue(entry, 2);
            const dstType = getPropertyValue(entry, 3);
            if (typeof src !== "string" || typeof dst !== "string") {
                continue;
            }
            if (srcType === "FbxPropertyEntry" && dstType === "FbxSemanticEntry") {
                add(dst, src);
            }
            else if (srcType === "FbxSemanticEntry" && dstType === "FbxPropertyEntry") {
                add(src, dst);
            }
        }
    }
    return { renderApi: typeof renderApi === "string" ? renderApi : "", bindings };
}
function extractMaterialProperties(materialNode, template) {
    const props = {};
    props.diffuseColor = getColorProperty(materialNode, template, "DiffuseColor") ?? getColorProperty(materialNode, template, "Diffuse");
    props.diffuseFactor = getNumberProperty(materialNode, template, "DiffuseFactor");
    props.ambientColor = getColorProperty(materialNode, template, "AmbientColor") ?? getColorProperty(materialNode, template, "Ambient");
    props.ambientFactor = getNumberProperty(materialNode, template, "AmbientFactor");
    props.specularColor = getColorProperty(materialNode, template, "SpecularColor") ?? getColorProperty(materialNode, template, "Specular");
    props.specularFactor = getNumberProperty(materialNode, template, "SpecularFactor");
    props.shininess = getNumberProperty(materialNode, template, "Shininess") ?? getNumberProperty(materialNode, template, "ShininessExponent");
    props.emissiveColor = getColorProperty(materialNode, template, "EmissiveColor") ?? getColorProperty(materialNode, template, "Emissive");
    props.emissiveFactor = getNumberProperty(materialNode, template, "EmissiveFactor");
    props.opacity = getNumberProperty(materialNode, template, "Opacity");
    props.transparencyFactor = getNumberProperty(materialNode, template, "TransparencyFactor");
    return props;
}
function extractTextures(materialId, objectMap, template) {
    const textures = [];
    const textureChildren = getChildren(objectMap, materialId, "Texture");
    for (const { id, node, propertyName } of textureChildren) {
        textures.push(extractTextureRef(id, node, propertyName, objectMap, template));
    }
    // LayeredTexture: several textures blended into one slot. Babylon has no layer blending, so the first layer
    // stands in for the stack; the layer count is recorded on the ref for diagnostics.
    for (const { id: layeredId, propertyName } of getChildren(objectMap, materialId, "LayeredTexture")) {
        const layers = getChildren(objectMap, layeredId, "Texture");
        if (layers.length === 0) {
            continue;
        }
        const ref = extractTextureRef(layers[0].id, layers[0].node, propertyName, objectMap, template);
        ref.layeredTextureId = layeredId;
        textures.push(ref);
    }
    return textures;
}
export function extractTextureRef(id, node, propertyName, objectMap, template) {
    {
        const fileNameNode = findChildByName(node, "FileName");
        const relFileNameNode = findChildByName(node, "RelativeFilename");
        const fileName = fileNameNode ? (getPropertyValue(fileNameNode, 0) ?? "") : "";
        const relativeFileName = relFileNameNode ? (getPropertyValue(relFileNameNode, 0) ?? "") : "";
        // Extract UV transform properties
        let uvTranslation;
        let uvScaling;
        const uvRotation = getNumberProperty(node, template, "UVRotation") ?? getNumberProperty(node, template, "Rotation");
        let uvSetName;
        uvTranslation = getTextureVector2(node, template, "UVTranslation") ?? getTextureVector2(node, template, "Translation");
        uvScaling = getTextureVector2(node, template, "UVScaling") ?? getTextureVector2(node, template, "Scaling");
        const uvSet = resolvePropertyValue(node, template, "UVSet");
        if (uvSet && uvSet.length > 0) {
            uvSetName = uvSet;
        }
        uvTranslation ??= getNumberPairChild(node, "ModelUVTranslation");
        uvScaling ??= getNumberPairChild(node, "ModelUVScaling");
        const wrapU = getNumberProperty(node, template, "WrapModeU");
        const wrapV = getNumberProperty(node, template, "WrapModeV");
        // Check for embedded texture data in connected Video node
        let embeddedData = null;
        const videoChildren = getChildren(objectMap, id, "Video");
        for (const { node: videoNode } of videoChildren) {
            const contentNode = findChildByName(videoNode, "Content");
            if (contentNode && contentNode.properties.length > 0) {
                const content = contentNode.properties[0].value;
                if (content instanceof Uint8Array && content.length > 0) {
                    embeddedData = content;
                }
                else if (content instanceof ArrayBuffer && content.byteLength > 0) {
                    embeddedData = new Uint8Array(content);
                }
            }
        }
        return {
            propertyName: propertyName ?? "DiffuseColor",
            fileName,
            relativeFileName,
            id,
            embeddedData,
            uvTranslation,
            uvScaling,
            uvRotation,
            uvSetName,
            wrapU,
            wrapV,
        };
    }
}
// ── Helpers ────────────────────────────────────────────────────────────────────
function getMaterialTemplate(materialNode, templates) {
    if (!templates) {
        return undefined;
    }
    const shadingModel = findChildByName(materialNode, "ShadingModel");
    const shadingType = shadingModel ? getPropertyValue(shadingModel, 0) : undefined;
    if (shadingType?.toLowerCase() === "phong") {
        return getPropertyTemplate(templates, "Material", "FbxSurfacePhong") ?? getPropertyTemplate(templates, "Material");
    }
    if (shadingType?.toLowerCase() === "lambert") {
        return getPropertyTemplate(templates, "Material", "FbxSurfaceLambert") ?? getPropertyTemplate(templates, "Material");
    }
    return getPropertyTemplate(templates, "Material");
}
function getColorProperty(node, template, propertyName) {
    const values = resolvePropertyValues(node, template, propertyName);
    if (!values || values.length < 3) {
        return undefined;
    }
    const r = toNumber(values[0]);
    const g = toNumber(values[1]);
    const b = toNumber(values[2]);
    if (r === undefined || g === undefined || b === undefined) {
        return undefined;
    }
    return [r, g, b];
}
function getNumberProperty(node, template, propertyName) {
    return toNumber(resolvePropertyValue(node, template, propertyName));
}
function getTextureVector2(node, template, propertyName) {
    const values = resolvePropertyValues(node, template, propertyName);
    if (!values) {
        return undefined;
    }
    const u = toNumber(values[0]);
    const v = toNumber(values[1]);
    return u !== undefined && v !== undefined ? [u, v] : undefined;
}
function toNumber(value) {
    if (typeof value === "number") {
        return value;
    }
    return undefined;
}
function getNumberPairChild(node, childName) {
    const child = findChildByName(node, childName);
    if (!child) {
        return undefined;
    }
    const u = toNumber(child.properties[0]?.value);
    const v = toNumber(child.properties[1]?.value);
    return u !== undefined && v !== undefined ? [u, v] : undefined;
}
//# sourceMappingURL=materials.js.map